import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { PmPerson } from "@arc/contracts";
import { pmBoardConfigFor } from "@arc/tenant-config";

type Institutes = ReadonlyArray<{ key: string; label: string }>;

const PmInstitutes = createContext<Institutes>([]);

/** The realm's institutes for everything inside the board. Their labels are the realm's own names, from its tenant config. */
export function PmInstitutesProvider({ realm, children }: { realm: string; children: ReactNode }) {
  const institutes = useMemo(() => pmBoardConfigFor(realm).institutes, [realm]);
  return <PmInstitutes.Provider value={institutes}>{children}</PmInstitutes.Provider>;
}

export function usePmInstitutes(): Institutes {
  return useContext(PmInstitutes);
}

/**
 * "Name · Institut" — the author label (D2); "ohne Institut" when none is assigned. A snapshot in the log can hold a key
 * that has since left the config, which then shows as the raw key.
 */
export function useInstituteLabel(): (person: Pick<PmPerson, "institute">) => string {
  const { t } = useTranslation();
  const institutes = usePmInstitutes();
  return useCallback(
    (person) => (person.institute ? (institutes.find((i) => i.key === person.institute)?.label ?? person.institute) : t("pmBoard.institute.none")),
    [institutes, t],
  );
}
